const ALL_BRANCH_DEVICES_PREFIX = "all:";
const ALL_RETAILER_DEVICES_PREFIX = "all-retailer:";

const normalizeMountType = (value) => String(value || "root").trim().toLowerCase();
const normalizeMountKey = (value) => String(value || "").trim();

export const getBranchKey = (retailerName, branchName) => `${retailerName}::${branchName}`;

// Split only on the first "::" so branch names may contain "::".
export const parseBranchKey = (branchKey) => {
  const value = String(branchKey || "").trim();
  const separatorIndex = value.indexOf("::");
  if (separatorIndex <= 0) return { retailerName: "", branchName: "" };
  return {
    retailerName: value.slice(0, separatorIndex).trim(),
    branchName: value.slice(separatorIndex + 2).trim(),
  };
};

export const allDevicesFolderId = (branchKey) =>
  `${ALL_BRANCH_DEVICES_PREFIX}${String(branchKey || "").trim()}`;
export const allRetailerDevicesFolderId = (retailerName) =>
  `${ALL_RETAILER_DEVICES_PREFIX}${String(retailerName || "").trim()}`;

export const parseAllDevicesFolderId = (id) => {
  if (!id || typeof id !== "string") return null;
  if (!id.startsWith(ALL_BRANCH_DEVICES_PREFIX)) return null;
  return id.slice(ALL_BRANCH_DEVICES_PREFIX.length);
};

export const parseAllRetailerDevicesFolderId = (id) => {
  if (!id || typeof id !== "string") return null;
  if (!id.startsWith(ALL_RETAILER_DEVICES_PREFIX)) return null;
  return id.slice(ALL_RETAILER_DEVICES_PREFIX.length);
};

/** Walk folder ancestors to find a branch mount; returns branch mount_key (retailer::branch). */
export const getBranchMountKeyFromFolderId = (folderId, foldersById) => {
  const id = String(folderId || "").trim();
  if (!id || typeof foldersById !== "object") return null;
  if (parseAllDevicesFolderId(id) || parseAllRetailerDevicesFolderId(id)) return null;
  const seen = new Set();
  let cursor = foldersById[id];
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    const mt = normalizeMountType(cursor.mount_type);
    if (mt === "branch") {
      return normalizeMountKey(cursor.mount_key) || null;
    }
    const parentId = String(cursor.parent_id || "").trim();
    if (!parentId) break;
    cursor = foldersById[parentId];
  }
  return null;
};

export const getFolderKey = (folder) => String(folder?.id || "");

export const getChildFolders = (folders, parentId) =>
  folders.filter((folder) => String(folder?.parent_id || "") === String(parentId || ""));

export const getRootMountedFolders = (folders) =>
  folders.filter(
    (folder) =>
      !String(folder?.parent_id || "").trim() && normalizeMountType(folder?.mount_type) === "root"
  );

export const getRetailerMountedFolders = (folders, retailerName) =>
  folders.filter(
    (folder) =>
      !String(folder?.parent_id || "").trim() &&
      normalizeMountType(folder?.mount_type) === "retailer" &&
      normalizeMountKey(folder?.mount_key) === String(retailerName || "").trim()
  );

export const getBranchMountedFolders = (folders, branchKey) =>
  folders.filter(
    (folder) =>
      !String(folder?.parent_id || "").trim() &&
      normalizeMountType(folder?.mount_type) === "branch" &&
      normalizeMountKey(folder?.mount_key) === String(branchKey || "").trim()
  );

export const buildFoldersById = (folders) =>
  folders.reduce((accumulator, folder) => {
    const id = String(folder?.id || "").trim();
    if (!id) return accumulator;
    accumulator[id] = folder;
    return accumulator;
  }, {});

const getFolderPathParts = (folderId, foldersById) => {
  const parts = [];
  const seen = new Set();
  let cursor = foldersById[String(folderId || "").trim()];
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    parts.unshift(cursor.name || cursor.id);
    cursor = foldersById[String(cursor.parent_id || "").trim()];
  }
  return parts;
};

const getFolderMountLabel = (folder) => {
  const mountType = normalizeMountType(folder?.mount_type);
  const mountKey = normalizeMountKey(folder?.mount_key);
  if (mountType === "retailer") return mountKey || "Retailer";
  if (mountType === "branch") return (mountKey || "Branch").replaceAll("::", " / ");
  return "Root";
};

export const getFolderFullLabel = (folderId, foldersById) => {
  const id = String(folderId || "").trim();
  if (!id) return "";
  const folder = foldersById[id];
  if (!folder) return id;
  const mountLabel = getFolderMountLabel(folder);
  const pathLabel = getFolderPathParts(id, foldersById).join(" / ") || id;
  return mountLabel ? `${mountLabel} / ${pathLabel}` : pathLabel;
};

export const buildFolderOptions = (folders, foldersById) =>
  folders
    .filter((folder) => Boolean(String(folder?.id || "").trim()))
    .map((folder) => ({
      id: String(folder.id),
      label: getFolderFullLabel(folder.id, foldersById),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const getDeviceDuplicateFolderPath = (deviceId, folderAssignments, foldersById) => {
  const folderId = String(folderAssignments?.[deviceId]?.folder_id || "").trim();
  if (!folderId) return "";
  return getFolderFullLabel(folderId, foldersById);
};

export const UNASSIGNED_FOLDER_FILTER_VALUE = "__unassigned__";

/** Leaf folder name for the device's current assignment (not the full path). */
export const getDeviceCurrentFolderName = (deviceId, folderAssignments, foldersById) => {
  const folderId = String(folderAssignments?.[deviceId]?.folder_id || "").trim();
  if (!folderId) return "";
  const folder = foldersById[folderId];
  return String(folder?.name || "").trim();
};

export const getDeviceCurrentFolderFilterValue = (deviceId, folderAssignments, foldersById) => {
  const name = getDeviceCurrentFolderName(deviceId, folderAssignments, foldersById);
  return name || UNASSIGNED_FOLDER_FILTER_VALUE;
};

/** Home branch from device cache tree position (retailer::branch). */
export const getDeviceHomeBranchKey = (retailerName, branchName) =>
  getBranchKey(retailerName, branchName);

/** True when folder is under the given branch mount (same retailer/branch). */
export const folderBelongsToBranchKey = (folderId, branchKey, foldersById) => {
  const mountKey = getBranchMountKeyFromFolderId(folderId, foldersById);
  if (!mountKey || !branchKey) return false;
  return mountKey === branchKey;
};
